import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

const UserStatusForm = ({ user, onSubmit, onCancel, loading }) => {
  const { t } = useTranslation(["users", "common"]);

  const [formData, setFormData] = useState({
    emailVerified: false,
    active: true,
    banned: false,
    role: "USER",
  });

  // Role options
  const roleOptions = [
    { value: "USER", label: t("users:roles.ROLE_USER") },
    { value: "ADMIN", label: t("users:roles.ROLE_ADMIN") },
    { value: "MODERATOR", label: t("users:roles.ROLE_MODERATOR") },
  ];

  // Load form data when user prop changes
  useEffect(() => {
    if (user) {
      setFormData({
        emailVerified: user.emailVerified || false,
        active: user.active !== undefined ? user.active : true,
        banned: user.banned || false,
        role: user.role?.replace("ROLE_", "").toUpperCase() || "USER",
      });
    }
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // Add this handler to properly handle form submission
  const handleSubmit = (e) => {
    e.preventDefault(); // Prevent default form submission
    onSubmit(formData); // Pass the form data to the parent
  };

  return (
    <form onSubmit={handleSubmit} className="user-status-form">
      {/* Rest of your form content remains the same */}
      <div className="row g-4">
        <div className="col-12">
          <div className="form-section">
            <div className="form-section-header">
              <i className="fas fa-user-shield"></i>
              <h6>{t("users:statusForm.header")}</h6>
            </div>

            <div className="row g-3">
              {/* Role Selection */}
              <div className="col-md-12 mb-3">
                <label className="form-label fw-bold">{t("users:statusForm.systemRole")}</label>
                <select
                  name="role"
                  className="form-select"
                  value={formData.role}
                  onChange={handleInputChange}
                  disabled={loading}
                >
                  {roleOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <small className="text-muted">
                  {t("users:statusForm.roleHint")}
                </small>
              </div>

              {/* Email Verified */}
              <div className="col-md-4">
                <div className="form-check form-switch">
                  <input
                    type="checkbox"
                    name="emailVerified"
                    className="form-check-input"
                    id="emailVerified"
                    checked={formData.emailVerified}
                    onChange={handleInputChange}
                    disabled={loading}
                  />
                  <label
                    className="form-check-label fw-medium"
                    htmlFor="emailVerified"
                  >
                    <i
                      className={`fas ${
                        formData.emailVerified
                          ? "fa-check-circle text-success"
                          : "fa-times-circle text-danger"
                      } me-2`}
                    ></i>
                    {t("users:statusForm.emailVerified")}
                  </label>
                </div>
              </div>

              {/* Active */}
              <div className="col-md-4">
                <div className="form-check form-switch">
                  <input
                    type="checkbox"
                    name="active"
                    className="form-check-input"
                    id="active"
                    checked={formData.active}
                    onChange={handleInputChange}
                    disabled={loading}
                  />
                  <label
                    className="form-check-label fw-medium"
                    htmlFor="active"
                  >
                    <i
                      className={`fas ${
                        formData.active
                          ? "fa-check-circle text-success"
                          : "fa-pause-circle text-secondary"
                      } me-2`}
                    ></i>
                    {t("users:statusForm.activeAccount")}
                  </label>
                </div>
              </div>

              {/* Is Banned */}
              <div className="col-md-4">
                <div className="form-check form-switch">
                  <input
                    type="checkbox"
                    name="banned"
                    className="form-check-input"
                    id="banned"
                    checked={formData.banned}
                    onChange={handleInputChange}
                    disabled={loading}
                  />
                  <label
                    className="form-check-label fw-medium"
                    htmlFor="banned"
                  >
                    <i
                      className={`fas ${
                        formData.banned
                          ? "fa-ban text-danger"
                          : "fa-check text-success"
                      } me-2`}
                    ></i>
                    {formData.banned ? t("users:statusForm.banned") : t("users:statusForm.notBanned")}
                  </label>
                </div>
              </div>
            </div>

            <div className="mt-3">
              <div className="alert alert-info">
                <i className="fas fa-info-circle me-2"></i>
                <strong>{t("users:statusForm.infoTitle")}</strong>
                <ul className="mb-0 mt-2">
                  <li>
                    <strong>{t("users:statusForm.emailVerified")}:</strong> {t("users:statusForm.infoEmailVerified")}
                  </li>
                  <li>
                    <strong>{t("users:statusForm.activeAccount")}:</strong> {t("users:statusForm.infoActiveAccount")}
                  </li>
                  <li>
                    <strong>{t("users:statusForm.banned")}:</strong> {t("users:statusForm.infoBanned")}
                  </li>
                  <li>
                    <strong>{t("users:statusForm.systemRole")}:</strong> {t("users:statusForm.infoSystemRole")}
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Form Actions */}
      <div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={onCancel}
          disabled={loading}
        >
          <i className="fas fa-times me-2"></i>
          {t("common:buttons.cancel")}
        </button>
        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
              ></span>
              {t("users:statusForm.updating")}
            </>
          ) : (
            <>
              <i className="fas fa-save me-2"></i>
              {t("users:statusForm.updateStatus")}
            </>
          )}
        </button>
      </div>
    </form>
  );
};

export default UserStatusForm;
